import { Check, Info } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { CTASection } from "@/components/CTASection";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { SERVICE_PAGES } from "@/data/services";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail({ slug }) {
  const page = SERVICE_PAGES[slug];
  if (!page) return <NotFound />;

  return (
    <>
      <SEO title={page.seoTitle} description={page.seoDescription} path={`/${slug}`} />
      <PageHero
        eyebrow={page.eyebrow}
        titleLines={page.titleLines}
        copy={page.intro}
        ctaLabel={page.ctaLabel}
        note="Currently serving Hyderabad & Greater Hyderabad"
      />

      {page.process && (
        <section className="border-b border-navy-900/10 bg-white py-20 sm:py-28" data-testid="service-process">
          <div className="container-site">
            <Reveal>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
                {page.process.heading}
              </h2>
            </Reveal>
            <div className="relative mt-12">
              <div className="absolute bottom-6 left-6 top-6 w-px bg-gold-500/40 sm:hidden" aria-hidden="true" />
              <div className="absolute left-0 right-0 top-6 hidden h-px bg-gradient-to-r from-gold-500/10 via-gold-500/50 to-gold-500/10 sm:block" aria-hidden="true" />
              <Stagger className={`grid gap-10 sm:grid-cols-2 ${page.process.steps.length > 4 ? "lg:grid-cols-5" : "lg:grid-cols-4"} lg:gap-6`} gap={0.1}>
                {page.process.steps.map((s, i) => (
                  <StaggerItem key={s.title}>
                    <div data-testid={`service-process-step-${i + 1}`}>
                      <span className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-gold-500/40 bg-white font-display text-sm font-extrabold text-gold-600">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="mt-5 font-display text-lg font-extrabold tracking-tight text-navy-900">{s.title}</h3>
                      <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{s.copy}</p>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </div>
        </section>
      )}

      <section className="bg-ivory py-20 sm:py-28">
        <div className="container-site space-y-16">
          {page.blocks.map((block, bi) => (
            <div key={bi} className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
              <Reveal>
                <div className="lg:sticky lg:top-32">
                  <span className="font-display text-xs font-bold tracking-[0.3em] text-gold-600">
                    {String(bi + 1).padStart(2, "0")}
                  </span>
                  <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-navy-900 sm:text-3xl">
                    {block.heading}
                  </h2>
                  {block.copy && (
                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">{block.copy}</p>
                  )}
                </div>
              </Reveal>
              {block.items?.length > 0 && (
                <Stagger className="grid gap-3 sm:grid-cols-2" gap={0.04}>
                  {block.items.map((item) => (
                    <StaggerItem key={item}>
                      <div className="flex h-full items-center gap-3 rounded-xl border border-navy-900/10 bg-white px-4 py-3.5">
                        <Check className="h-4 w-4 shrink-0 text-gold-600" />
                        <span className="text-sm font-medium text-navy-900/85">{item}</span>
                      </div>
                    </StaggerItem>
                  ))}
                </Stagger>
              )}
            </div>
          ))}

          {page.note && (
            <Reveal>
              <div className="flex gap-4 rounded-2xl border border-gold-500/30 bg-gold-300/15 p-6 sm:p-8" data-testid="service-note">
                <Info className="mt-0.5 h-5 w-5 shrink-0 text-gold-600" />
                <p className="text-xs leading-relaxed text-navy-900/75 sm:text-sm">{page.note}</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <CTASection />
    </>
  );
}
